import "./Header.scss";
import React, { useState, useEffect } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearUser } from "../../state/auth/authSlice";
import { motion, AnimatePresence } from "framer-motion";
import { FiMenu, FiX } from "react-icons/fi";
import Button from "../Button/Button";
import erpLogo from "../../assets/images/erplogo.png";

const Header = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const isAuthenticated = () => {
    return localStorage.getItem("ERPTokens") !== null;
  };

  const handleAuthAction = () => {
    if (isAuthenticated()) {
      localStorage.removeItem("ERPTokens");
      dispatch(clearUser());
      navigate("/login");
    } else {
      navigate("/login");
    }
  };

  const navigationLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About Us" },
    {
      path: "https://lancehawks.com/",
      label: "Lancehawks",
      external: true,
    },
    { path: "/contact", label: "Contact Us" },
  ];

  return (
    <header className={`header ${isScrolled ? "header--scrolled" : ""}`}>
      <div className="header__container">
        {/* Logo */}
        <motion.div
          className="header__logo"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Link to="/">
            <img src={erpLogo} alt="Lancehawks ERP" />
          </Link>
        </motion.div>

        {/* Desktop Navigation */}
        <nav className="header__nav">
          {navigationLinks.map((link, index) =>
            link.external ? (
              <a
                key={index}
                href={link.path}
                target="_blank"
                rel="noopener noreferrer"
                className="header__nav-link"
              >
                {link.label}
              </a>
            ) : (
              <NavLink
                key={index}
                to={link.path}
                className={({ isActive }) =>
                  `header__nav-link ${
                    isActive ? "header__nav-link--active" : ""
                  }`
                }
              >
                {link.label}
              </NavLink>
            )
          )}
        </nav>

        {/* Auth Button */}
        <div className="header__auth">
          <Button
            text={isAuthenticated() ? "Logout" : "Login"}
            type={isAuthenticated() ? "red" : "primary"}
            onClick={handleAuthAction}
          />
        </div>

        {/* Mobile Menu Button */}
        <button
          className="header__mobile-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              className="header__mobile-nav"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
            >
              {navigationLinks.map((link, index) =>
                link.external ? (
                  <a
                    key={index}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="header__mobile-nav-link"
                  >
                    {link.label}
                  </a>
                ) : (
                  <NavLink
                    key={index}
                    to={link.path}
                    className={({ isActive }) =>
                      `header__mobile-nav-link ${
                        isActive ? "header__mobile-nav-link--active" : ""
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                )
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
